'use client';

import { useTranslations } from 'next-intl';
import { useEffect, useRef, useState } from 'react';
import { BaseLineCopy } from '@/app/components/icons/BaseLineCopy';
import { BaseLineEdit } from '@/app/components/icons/BaseLineEdit';
import { CustomTooltip } from '@/app/components/shared/CustomTooltip';
import { GlobalLoader } from '@/app/components/shared/GlobalLoader';
import { UserAvatar } from '@/app/components/shared/UserAvatar';
import { Button } from '@/app/components/ui/button';
import { useCopy, useFetch } from '@/app/hooks';
import { useStore } from '@/app/providers/store';
import { Link, useRouter } from '@/i18n/routing';
import { LiveRecordList } from '../_live-records/_components';
import { OfflineAvatarTaskList } from '../_offline-avatar';
import { getStreamProfile } from '../apis';
import { StreamProfileEditDialog } from './StreamProfileEditDialog';

type StreamProfileTab = 'live-records' | 'offline-avatar';

const PROFILE_TABS: Array<{ value: StreamProfileTab; href: string }> = [
  { value: 'live-records', href: '/vidu-stream/profile/live-records' },
  { value: 'offline-avatar', href: '/vidu-stream/profile/offline-avatar' },
];

export const StreamProfile = ({ activeTab }: { activeTab: StreamProfileTab }) => {
  const t = useTranslations();
  const { copy } = useCopy();
  const router = useRouter();
  const { userInfo, isUserInfoInitialized } = useStore((state) => ({
    userInfo: state.userInfo,
    isUserInfoInitialized: state.isUserInfoInitialized,
  }));
  const scrollRef = useRef<HTMLDivElement>(null);
  const [ready, setReady] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const { run } = useFetch(getStreamProfile, { manual: true, onFinally: () => setReady(true) });

  useEffect(() => {
    if (isUserInfoInitialized && !userInfo) {
      router.replace('/vidu-stream');
    }
  }, [isUserInfoInitialized, router, userInfo]);

  useEffect(() => {
    if (userInfo?.id) run();
  }, [userInfo?.id, run]);

  if (!isUserInfoInitialized || !userInfo || !ready) return <GlobalLoader />;

  const profileTabs = (
    <div className="flex h-14 items-center gap-6 border-system-white08 border-b bg-black">
      {PROFILE_TABS.map((tab) => {
        const active = activeTab === tab.value;
        return (
          <Link
            key={tab.value}
            href={tab.href}
            aria-current={active ? 'page' : undefined}
            className={`relative flex h-full items-center font-medium text-base transition-colors ${
              active ? 'text-white' : 'text-system-white48 hover:text-system-white80'
            }`}
          >
            {tab.value === 'live-records' ? t('sidebar.assetsItems.liveRecords') : t('tools.offlineDigitalHuman.title')}
            {active ? <span className="absolute inset-x-0 bottom-0 h-0.5 rounded-full bg-white" /> : null}
          </Link>
        );
      })}
    </div>
  );

  return (
    <div
      ref={scrollRef}
      id="live-record-list-scroll"
      className="max-content flex h-full min-h-0 w-full flex-col overflow-y-auto px-12 pb-16 text-white sm:px-6 sm:pb-12"
    >
      <section className="mt-16 mb-8 flex items-center gap-5 border-system-white12 border-b pb-8 sm:mt-12 sm:flex-col sm:text-center">
        <UserAvatar userInfo={userInfo} className="size-20" />
        <div className="flex flex-1 flex-col gap-2 sm:mt-2 sm:w-full sm:gap-1">
          <div className="flex items-center gap-2 sm:justify-center">
            <h1 className="font-medium text-2xl">{userInfo.nickname || '--'}</h1>
            <CustomTooltip
              content={
                <div className="flex items-center gap-1">
                  {userInfo.id}
                  <BaseLineCopy className="cursor-pointer" onClick={() => copy(userInfo.id || '')} />
                </div>
              }
            >
              <div
                className="flex size-6 cursor-pointer items-center justify-center rounded-4 bg-system-white/10 text-sm text-system-white/70"
                onClick={() => copy(userInfo.id || '')}
              >
                ID
              </div>
            </CustomTooltip>
          </div>
          <p className="mt-2 text-sm text-system-white64">{userInfo.biography || t('profile.biography.placeholder')}</p>
        </div>
        <Button
          variant="ghost"
          className="h-8 rounded-8 bg-system-white10 px-3 font-semibold text-sm hover:bg-system-white20 hover:text-system-white sm:h-10 sm:w-full"
          iconClassName="sm:block"
          icon={<BaseLineEdit className="mr-1 size-[18px] sm:mr-2" />}
          onClick={() => setEditOpen(true)}
        >
          {t('personal.accountSetting')}
        </Button>
      </section>
      {activeTab === 'live-records' ? (
        <LiveRecordList embedded scrollRef={scrollRef} stickyHeader={profileTabs} />
      ) : (
        <>
          <div className="sticky top-0 z-30 mb-4 bg-black">{profileTabs}</div>
          <OfflineAvatarTaskList />
        </>
      )}
      <StreamProfileEditDialog open={editOpen} onClose={() => setEditOpen(false)} />
    </div>
  );
};
